import axios from 'axios';

// Admin auth plumbing: token storage + global axios interceptors. The token comes from the
// LOGIN action folded into the livestream endpoint (see lambda-cluster/adminAuthGuard.js).
const TOKEN_KEY = 'prodpanelAdminToken';
const LOGIN_ENDPOINT =
    'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/livestream-3820238';

// Only our API Gateway endpoints get the admin token — never third-party URLs.
const API_HOST_PATTERN = /execute-api\.ap-southeast-1\.amazonaws\.com/;

function decodeExp(token: string): number | null {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return typeof payload.exp === 'number' ? payload.exp : null;
    } catch (e) {
        return null;
    }
}

// Returns the stored token, discarding it when expired/undecodable.
export function getStoredToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    const exp = decodeExp(token);
    if (!exp || exp <= Date.now() / 1000) {
        localStorage.removeItem(TOKEN_KEY);
        return null;
    }
    return token;
}

export function clearStoredToken() {
    localStorage.removeItem(TOKEN_KEY);
}

export async function adminLogin(username: string, password: string): Promise<string> {
    try {
        const response = await axios({
            method: 'post',
            url: LOGIN_ENDPOINT,
            data: JSON.stringify({ adminAuthAction: 'LOGIN', payload: { username, password } }),
        });
        const token = response.data?.token;
        if (!token) throw new Error(response.data?.message ?? 'Login gagal');
        localStorage.setItem(TOKEN_KEY, token);
        return token;
    } catch (error: any) {
        throw new Error(error?.response?.data?.message ?? error?.message ?? 'Login gagal');
    }
}

let interceptorsInstalled = false;

// Attaches the admin token to every API Gateway request and forces a re-login when the
// backend answers with an authDenied 403 (expired/rotated token). Install once, before App.
export function installAdminAuthInterceptors() {
    if (interceptorsInstalled) return;
    interceptorsInstalled = true;

    axios.interceptors.request.use((config) => {
        const token = getStoredToken();
        if (token && API_HOST_PATTERN.test(config.url ?? '')) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error?.response?.data?.authDenied) {
                // Reload with the token cleared so the login gate comes back with clean state.
                clearStoredToken();
                window.location.reload();
            }
            return Promise.reject(error);
        }
    );
}
